<template>
  <div class="debug-info">
    <div v-if="toasts.length === 0">No toasts to display</div>
    <div v-else>Toasts: {{ toasts.length }}</div>
  </div>
  
  <div 
    v-if="hasQuestionToast" 
    class="toast-overlay"
  ></div>
  
  <div class="custom-toast-wrapper" role="alert" aria-live="assertive" aria-atomic="true">
    <transition-group 
      tag="div" 
      name="toast-animation" 
      class="custom-toast-container"
      :class="{'centered-container': hasQuestionToast}"
    >
      <div 
        v-for="toast in toasts" 
        :key="toast.id" 
        :class="['custom-toast', `toast-${toast.severity}`, {'toast-question-centered': toast.severity === 'question'}]"
        @click="toast.actions ? null : removeToast(toast.id)"
        tabindex="0"
        @keydown.enter="toast.actions ? null : removeToast(toast.id)"
        @keydown.esc="removeToast(toast.id)"
      >
        <div class="toast-icon">
          <i :class="getIconClass(toast.severity)" aria-hidden="true"></i>
        </div>
        <div class="toast-content">
          <div class="toast-summary">{{ toast.summary }}</div>
          <div class="toast-detail" v-html="toast.detail"></div>
          
          <!-- Botones de acción si existen -->
          <div v-if="toast.actions && toast.actions.length > 0" class="toast-actions">
            <button 
              v-for="(action, index) in toast.actions" 
              :key="index"
              :class="['p-button', action.class]"
              @click.stop="handleActionClick(toast.id, action)"
            >
              {{ action.label }}
            </button>
          </div>
        </div>
        <div 
          class="toast-close" 
          @click.stop="removeToast(toast.id)"
          tabindex="0"
          role="button"
          aria-label="Close notification"
          @keydown.enter="removeToast(toast.id)"
        >
          <i class="pi pi-times" aria-hidden="true"></i>
        </div>
        <div 
          v-if="toast.life !== Infinity"
          class="toast-progress-container"
          aria-hidden="true"
        >
          <div 
            class="toast-progress" 
            :style="{ 
              animationDuration: `${toast.life}ms`,
              width: getProgressWidth(toast)
            }"
          ></div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
/**
 * @component CustomToast
 * @description Componente para mostrar notificaciones tipo toast con diferentes niveles de severidad,
 * animaciones y tiempos de vida configurables. Proporciona una interfaz elegante para feedback al usuario.
 */
let toastId = 0;

export default {
  name: 'CustomToast',
  data() {
    return {
      toasts: [],
      defaultLife: 3000,
      counter: 0,
      progressIntervals: {}
    };
  },
  computed: {
    hasQuestionToast() {
      return this.toasts.some(toast => toast.severity === 'question');
    }
  },
  methods: {
    /**
     * Añade un nuevo toast a la cola de visualización
     * @param {Object} toast - Configuración del toast
     * @param {string} toast.severity - Nivel de severidad (info, success, warn, error)
     * @param {string} toast.summary - Título del toast
     * @param {string} toast.detail - Descripción detallada
     * @param {number} toast.life - Duración en milisegundos
     * @param {string} toast.group - Grupo al que pertenece (para eliminación grupal)
     * @param {Array} toast.actions - Botones de acción [{ label, class, handler }]
     * @returns {number} - ID único del toast creado
     */
    add(toast) {
      try {
        
        const id = toastId++;
        const newToast = {
          id,
          severity: toast.severity || 'info',
          summary: toast.summary || '',
          detail: toast.detail || '',
          life: toast.actions ? Infinity : (toast.life || this.defaultLife),
          group: toast.group || null,
          remainingTime: toast.life || this.defaultLife,
          actions: toast.actions || []
        };

        this.toasts.push(newToast);
        
        if (newToast.life !== Infinity) {
          // Start tracking progress for this toast
          this.trackProgress(newToast);
          
          setTimeout(() => {
            
            this.removeToast(id);
          }, newToast.life);
        }
        
        return id;
      } catch (error) {
        
        return -1;
      }
    },

    /**
     * Elimina un toast específico por su ID
     * @param {number} id - ID del toast a eliminar
     */
    removeToast(id) {
      try {
        
        // Clear the progress interval when removing the toast
        if (this.progressIntervals[id]) {
          clearInterval(this.progressIntervals[id]);
          delete this.progressIntervals[id];
        }
        
        const index = this.toasts.findIndex(toast => toast.id === id);
        if (index !== -1) {
          this.toasts.splice(index, 1);
        }
      } catch (error) {
        
      }
    },

    /**
     * Obtiene la clase de icono según la severidad
     * @param {string} severity - Nivel de severidad
     * @returns {string} - Clase CSS del icono
     */
    getIconClass(severity) {
      try {
        switch (severity) {
          case 'success': return 'pi pi-check-circle';
          case 'info': return 'pi pi-info-circle';
          case 'warn': return 'pi pi-exclamation-triangle';
          case 'error': return 'pi pi-times-circle';
          case 'question': return 'pi pi-question-circle';
          default: return 'pi pi-info-circle';
        }
      } catch (error) {
        
        return 'pi pi-info-circle';
      }
    },

    /**
     * Elimina todos los toasts
     */
    removeAll() {
      try {
        
        // Limpiar todos los intervalos primero
        Object.keys(this.progressIntervals).forEach(id => {
          clearInterval(this.progressIntervals[id]);
        });
        this.progressIntervals = {};
        this.toasts = [];
      } catch (error) {
        
      }
    },

    /**
     * Elimina todos los toasts de un grupo específico
     * @param {string} group - Nombre del grupo
     */
    removeByGroup(group) {
      try {
        if (!group) return;

        // Identificar los IDs de toasts a eliminar
        const toastsToRemove = this.toasts.filter(toast => toast.group === group);
        
        // Limpiar sus intervalos
        toastsToRemove.forEach(toast => {
          if (this.progressIntervals[toast.id]) {
            clearInterval(this.progressIntervals[toast.id]);
            delete this.progressIntervals[toast.id];
          }
        });
        
        // Filtrar la lista de toasts
        this.toasts = this.toasts.filter(toast => toast.group !== group);
      } catch (error) {
        
      }
    },

    /**
     * Rastrea el progreso de un toast para actualizar su barra de progreso
     * @param {Object} toast - Toast a rastrear
     */
    trackProgress(toast) {
      try {
        // Update remaining time every 100ms
        const updateInterval = 100;
        const startTime = Date.now();
        const endTime = startTime + toast.life;
        
        this.progressIntervals[toast.id] = setInterval(() => {
          const now = Date.now();
          const remaining = Math.max(0, endTime - now);
          
          // Find and update the toast
          const toastToUpdate = this.toasts.find(t => t.id === toast.id);
          if (toastToUpdate) {
            toastToUpdate.remainingTime = remaining;
          }
          
          // Clear interval if time is up
          if (remaining <= 0) {
            clearInterval(this.progressIntervals[toast.id]);
            delete this.progressIntervals[toast.id];
          }
        }, updateInterval);
      } catch (error) {
        
      }
    },
    getProgressWidth(toast) {
      if (toast.life === Infinity) return '100%';
      const percentage = (toast.remainingTime / toast.life) * 100;
      return `${percentage}%`;
    },
    /**
     * Cierra todos los toasts de tipo pregunta
     */
    closeAllQuestions() {
      this.toasts
        .filter(toast => toast.severity === 'question')
        .forEach(toast => this.removeToast(toast.id));
    },
    
    /**
     * Maneja el clic en los botones de acción
     * @param {number} id - ID del toast
     * @param {Object} action - Configuración de la acción
     */
    handleActionClick(id, action) {
      try {
        // Eliminar el toast inmediatamente antes de ejecutar el handler
        this.removeToast(id);
        
        // Ejecutar el handler si existe
        if (action.handler) {
          action.handler();
        }
      } catch (error) {
        
        // Asegurar que el toast se elimine incluso si hay un error
        this.removeToast(id);
      }
    },
  },
  beforeUnmount() {
    // Clean up all intervals when component is destroyed
    Object.keys(this.progressIntervals).forEach(id => {
      clearInterval(this.progressIntervals[id]);
    });
  },
  mounted() {

    // Add a test toast to verify visibility
    // setTimeout(() => {
    //   this.add({
    //     severity: 'info',
    //     summary: 'Component Test',
    //     detail: 'Toast component is working!',
    //     life: 5000
    //   });
    // }, 500);
  }
};
</script>

<style scoped>
/* Debug styles - will help see if the component is rendering */
.debug-info {
  display: none; /* Change to 'block' to show debugging info */
  position: fixed;
  bottom: 10px;
  left: 10px;
  background: #333;
  color: white;
  padding: 5px;
  z-index: 10000;
  font-size: 12px;
}

/* Ensure the wrapper takes up no space but positions the toasts correctly */
.custom-toast-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  z-index: 9999;
}

/* Toast container - more direct positioning */
.custom-toast-container {
  position: fixed !important; /* Use !important to override any other styles */
  top: 20px !important;
  right: 20px !important;
  z-index: 9999 !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 10px !important;
  max-width: 400px !important;
  width: calc(100% - 40px) !important;
  pointer-events: auto !important;
}

/* Toast styling with corporate look */
.custom-toast {
  display: flex !important;
  align-items: flex-start !important;
  padding: var(--toast-padding, 16px) !important;
  border-radius: var(--toast-border-radius, 4px) !important;
  box-shadow: var(--toast-box-shadow, 0 2px 8px rgba(0, 0, 0, 0.15)) !important;
  background-color: var(--toast-background-color, white) !important;
  color: var(--text-color, black) !important;
  position: relative !important;
  overflow: hidden !important;
  cursor: pointer !important;
  min-width: var(--toast-min-width, 300px) !important;
  transform: none !important;
  opacity: 1 !important;
  pointer-events: auto !important;
  border: 1px solid var(--border-color, #e0e0e0) !important;
  font-family: var(--font-body, -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji") !important;
  font-weight: var(--body-weight, 400) !important;
}

.custom-toast:hover {
  box-shadow: var(--toast-box-shadow-hover, 0 4px 12px rgba(0, 0, 0, 0.2));
}

.custom-toast:focus {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.toast-icon {
  flex-shrink: 0 !important;
  font-size: 20px !important;
  margin-right: 12px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-content {
  flex-grow: 1 !important;
  padding-right: 16px;
}

.toast-summary {
  font-weight: var(--bold-weight, 600) !important;
  margin-bottom: 4px !important;
  font-size: 15px !important;
}

.toast-detail {
  font-size: 14px !important;
  line-height: 1.4 !important;
  font-weight: var(--body-weight, 400) !important;
}

.toast-close {
  position: absolute !important;
  top: 12px !important;
  right: 12px !important;
  opacity: 0.7 !important;
  cursor: pointer !important;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s, opacity 0.2s;
}

.toast-close:hover {
  opacity: 1;
  background-color: var(--toast-close-hover-bg, rgba(0, 0, 0, 0.1));
}

.toast-close:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--primary-color);
}

.toast-progress-container {
  position: absolute !important;
  bottom: 0 !important;
  left: 0 !important;
  height: 4px !important;
  width: 100% !important;
  background-color: rgba(0, 0, 0, 0.05) !important;
  overflow: hidden !important;
}

.toast-progress {
  height: 100% !important;
  transform-origin: left !important;
  transition: width 0.1s linear !important;
}

/* Toast Severity Colors - More corporate solid colors */
.toast-info {
  border-left: 4px solid #2196f3 !important;
  background-color: #ffffff !important;
}

.toast-info .toast-icon {
  color: #2196f3 !important;
}

.toast-info .toast-progress {
  background-color: #2196f3 !important;
}

.toast-success {
  border-left: 4px solid #4caf50 !important;
  background-color: #ffffff !important;
}

.toast-success .toast-icon {
  color: #4caf50 !important;
}

.toast-success .toast-progress {
  background-color: #4caf50 !important;
}

.toast-warn {
  border-left: 4px solid #ff9800 !important;
  background-color: #ffffff !important;
}

.toast-warn .toast-icon {
  color: #ff9800 !important;
}

.toast-warn .toast-progress {
  background-color: #ff9800 !important;
}

.toast-error {
  border-left: 4px solid #f44336 !important;
  background-color: #ffffff !important;
}

.toast-error .toast-icon {
  color: #f44336 !important;
}

.toast-error .toast-progress {
  background-color: #f44336 !important;
}

/* Improved Animations */
.toast-animation-enter-active {
  /* transform: translateX(100%); */
  /* animation: slide-in 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards; */
}

.toast-animation-leave-active {
  /* animation: fade-out 0.3s cubic-bezier(0.55, 0.085, 0.68, 0.53) forwards; */
}

@keyframes slide-in {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes fade-out {
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-30px); }
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .custom-toast-container {
    width: calc(100% - 20px);
    right: 10px;
    top: 10px;
  }
  
  .toast-summary {
    font-size: 14px;
  }
  
  .toast-detail {
    font-size: 12px;
  }

  .custom-toast {
    min-width: auto;
  }
}

/* Support for dark mode */
body.dark .custom-toast {
  background-color: #333333 !important;
  border-color: #444444 !important;
  color: #e0e0e0 !important;
}

body.dark .toast-progress-container {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* Estilos para los botones de acción */
.toast-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  justify-content: space-around;
}

.toast-actions button {
  padding: 6px 12px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: background-color 0.2s;
}

.p-button-success {
  background-color: #4caf50;
  color: white;
}

.p-button-success:hover {
  background-color: #3d8b40;
}

.p-button-secondary {
  background-color: #757575;
  color: white;
}

.p-button-secondary:hover {
  background-color: #616161;
}

.p-button-sm {
  font-size: 12px;
  padding: 4px 10px;
}

/* Toasts con acciones requieren más espacio */
.custom-toast:has(.toast-actions) {
  min-height: 120px;
}

/* Evitar que el progreso se muestre en toasts con acciones */
.custom-toast:has(.toast-actions) .toast-progress-container {
  display: none;
}

/* Overlay con blur */
.toast-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9998;
  /* animation: fade-in 0.2s ease-out forwards; */
}

/* Contenedor centrado para preguntas */
.centered-container {
  top: 50% !important;
  right: 50% !important;
  transform: translate(50%, -50%) !important;
  width: auto !important;
  max-width: 450px !important;
  align-items: center !important;
}

/* Estilos específicos para toasts tipo question */
.toast-question-centered {
  min-width: 350px !important;
  max-width: 90vw !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25) !important;
  cursor: default !important;
  z-index: 9999 !important;
}

.toast-question {
  border-left: 4px solid #9c27b0 !important;
  background-color: #ffffff !important;
}

.toast-question .toast-icon {
  color: #9c27b0 !important;
}

.toast-question .toast-progress {
  background-color: #9c27b0 !important;
}

/* Animación para el overlay */
@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
